import React, { createContext, useContext } from 'react'

import { getAllMyPets } from '../services/petService'
import { getPetsLocally } from '../services/petProfileService'
import { getServices } from '../services/serviceService'

import MODAL from '../utils/modals'

import { focus, splitServicesList } from '../utils'
import { DEFAULT_ERROR } from '../utils/schedulingConstants'

import { useServiceScheduleContext, actionTypes } from './serviceScheduleContext'
import { serviceClickEvent } from '../utils/gtag'

export const petAndServicesContext = createContext()

// contexto de estado
export function PetAndServicesContextProvider ({ children }) {
  const { state, dispatch } = useServiceScheduleContext()

  const retrievePetsData = async (props) => {
    const { context } = props

    context.showLoader()
    const petsList =
      (context.firstName && (await getAllMyPets())) || getPetsLocally()

    dispatch({ type: actionTypes.UPDATE_STATE, payload: { petsData: petsList } })

    context.hideLoader()
  }

  const retrieveServiceData = async (props) => {
    const { context, router } = props

    context.showLoader()
    if (router?.query?.category || props?.category) {
      const serviceList = await getServices(
        (router?.query && router?.query?.category) || props?.category
          ? router?.query?.category
          : props?.category
            ? props?.category
            : undefined
      )

      if (serviceList === undefined || serviceList === null) {
        dispatch({ type: actionTypes.UPDATE_STATE, payload: { serviceData: [] } })
        context.hideLoader()
        return
      }

      const formattedServiceList = serviceList.map(
        ({ id, name, additional, category }) => ({
          value: id,
          name,
          category,
          photo: undefined,
          additional
        })
      )
      const {
        additionalServices,
        mainServices
      } = splitServicesList(formattedServiceList)

      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          serviceData: mainServices,
          additionalServiceData: additionalServices
        }
      })
    }
    context.hideLoader()
  }

  const onPetSelection = async (pet) => {
    const { scheduleBody, error } = state
    const newError = Object.assign({}, error)
    newError.pet_id = pet ? undefined : DEFAULT_ERROR.pet_id
    newError.unavailabilityPets = []

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: pet.id,
          professional_id: ''
        },
        error: newError,
        selectedPet: pet
      }
    })
  }

  const handleSelectedMyPet = async (pet, props) => {
    // verifica dados para atualizar cadastro do pet
    if (!pet.temperament || typeof pet.castrated !== 'boolean' || !pet.breed?.id || !pet.breed?.type || !pet.weight || !pet.gender) {
      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          modal: MODAL
            .INCOMPLETE_PET_FORM(
              pet,
              () => dispatch({
                type: actionTypes.UPDATE_STATE,
                payload: {
                  modal: MODAL.PET_SAVE_SUCCESS()
                }
              }))
        }
      })
    } else {
      // seleciona pet
      await onPetSelection(pet)
    }
  }

  const handleUnselectPet = () => {
    const { scheduleBody } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: undefined,
          professional_id: ''
        },
        selectedPet: undefined
      }
    })
  }

  const handleSelectedService = async (serviceId, focusRef, props) => {
    const { scheduleBody, error, rescheduleDataLoaded, changeStore } = state
    const newError = Object.assign({}, error)

    newError.service_id = serviceId ? undefined : DEFAULT_ERROR.service_id

    newError.unavailabilityPets = []

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          service_id: serviceId,
          samePetDate: changeStore ? false : undefined
        },
        error: newError
      }
    })

    if (focusRef && (!props?.router?.query?.reschedule || !!rescheduleDataLoaded)) {
      focus(focusRef, 'smooth', true)
    }

    serviceClickEvent(props?.serviceName)
  }

  const handleToggleAllAdditional = async (focusRef, props) => {
    const {
      scheduleBody,
      isAllAdditionalServicesSelected,
      additionalServiceData,
      rescheduleDataLoaded,
      changeStore
    } = state
    const { router } = props
    let updatedAdditionalServiceList = []
    if (!isAllAdditionalServicesSelected) {
      updatedAdditionalServiceList = additionalServiceData.map(
        (service) => service.value
      )
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        isAllAdditionalServicesSelected: !isAllAdditionalServicesSelected,
        scheduleBody: {
          ...scheduleBody,
          additional_services: updatedAdditionalServiceList,
          partner_id: undefined,
          samePetDate: changeStore ? false : undefined
        }
      }
    })

    if (focusRef && (!router.query.reschedule || !!rescheduleDataLoaded)) focus(focusRef, 'smooth')
  }

  const handleToggleAdditional = async (serviceId) => {
    const { scheduleBody, additionalServiceData, changeStore } = state
    const additionalServices = scheduleBody.additional_services
    const isChecked = additionalServices.includes(serviceId)
    const newAdditionalServices = !isChecked
      ? [...additionalServices, serviceId]
      : additionalServices.filter((service) => service !== serviceId)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          additional_services: newAdditionalServices,
          partner_id: undefined,
          samePetDate: changeStore ? false : undefined
        },
        isAllAdditionalServicesSelected:
          newAdditionalServices.length === additionalServiceData.length
      }
    })
  }

  const functions = {
    retrievePetsData,
    retrieveServiceData,
    handleSelectedMyPet,
    handleSelectedService,
    handleToggleAllAdditional,
    handleToggleAdditional,
    handleUnselectPet,
    onPetSelection
  }

  return (
    <petAndServicesContext.Provider value={{ functions }}>
      {children}
    </petAndServicesContext.Provider>
  )
}

// Hook para acessar o contexto
export function usePetAndServicesContext () {
  const context = useContext(petAndServicesContext)
  if (!context) {
    throw new Error('usePetAndServicesContext deve ser usado dentro de um Provider!')
  }

  const { functions } = context
  return { functions }
}
